/**
 * 작성자 : 박승호
 * 날짜 : 2025.01.03
 * 경로 : 사내용 상황판 > 긴급 공지
 */

import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import IssueGuide from '../IssueGuide';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../services/_common';
import SkeletonTable from '../SkeletonTable';
import Carousel from 'react-multi-carousel';
import { CustomDot } from './Carousel';
import { useEffect, useMemo, useRef, useState } from 'react';
import TextEditor from '../TextEditor';
import dayjs from 'dayjs';

const Root = styled.div`
  user-select: none;
  height: 100%;

  > .widget-headers {
    display: none !important;

    width: 100%;
    padding: 0.75rem 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex-grow: 0;

    &.margin-top {
      margin-bottom: 0;
    }
    > .widget-title {
      padding: 0 1rem;
      align-items: center;
    }
  }

  > .widget-body {
    &.card {
      width: 100%;
      height: calc(100% - 3.5rem);
      padding: 1.5rem;
      // padding: 0 1rem 1rem;
      flex-grow: 1;

      .react-multi-carousel-list,
      .react-multi-carousel-track {
        height: 100%;
      }

      .notice-box {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;

        .notice-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
          padding: 0 0 1rem 0;
          color: var(--text-primary);

          .notice-info {
            span {
              font-size: var(--widget-font-size-base);
              line-height: 1.2;
            }
          }
          h4 {
            font-size: var(--widget-font-size-lg);
            font-weight: 600;
            line-height: 1.2;
            white-space: break-spaces;
          }
        }

        /* ckeditor custom style */
        .ck-editor-cont {
          height: 100%;
          overflow: hidden;
          border-top: 1px solid var(--outline);
          padding: 0;

          .ckeditor {
            height: 100%;

            /* 에디터 내용 컨테이너 최소 높이 기본값 적용 ( 최소 높이로 인해 컨텐츠 내용이 적어도 자동스크롤 발생 방지 ) */
            .editor-container__editor {
              min-height: inherit;
            }

            /* 에디터 상단 툴바 제거 */
            .ck-editor__top {
              display: none;
            }

            .ck-content {
              .text-big {
                font-size: var(--widget-font-size-lg);
              }

              li {
                white-space: normal;
                word-break: keep-all;
              }
            }

            .ck-rounded-corners .ck.ck-editor__main > .ck-editor__editable,
            .ck.ck-editor__main > .ck-editor__editable.ck-rounded-corners {
              height: 100%;
              border: none;
              padding: 0;
              overflow: hidden;
              white-space: normal;
            }
          }
        }
      }

      .no-data {
        width: 100%;
        height: 100%;
        border: 1px solid var(--outline);
        border-radius: 0.5rem;
      }
    }
  }

  @keyframes urgentSlideUp {
    0% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(calc(-100% + 199px));
    }
  }
`;

interface Iprops {
  name: string;
  userInfo: any;
  refreshTimeCount: number;
}

const ANIMATION_DELAY = 3; // 애니메이션 딜레이 시간 / 3 = 3초 or 3 * 1000 = 3000ms = 3s;

const UrgentNotice = ({ name, userInfo, refreshTimeCount }: Iprops) => {
  const { t } = useTranslation();
  const [curIdx, setCurIdx] = useState(0); // carousel 현재 인덱스 - carousel 슬라이드 변경시 애니메이션 초기화를 위해 사용
  const ckRefs = useRef<(HTMLDivElement | null)[]>([]);
  const activeAnimations = useRef<Map<HTMLDivElement, Animation>>(new Map()); // 활성 애니메이션 추적

  const {
    data: urgent,
    isLoading,
    isError,
  } = useQuery(['/safe/curgent_dashboard'], () => getCurgentAPI(), {
    enabled: !!userInfo.hCd,
    refetchInterval: refreshTimeCount * 1000, // 30분마다 재호출
    refetchIntervalInBackground: true, // 포커싱 안되어 있어도 refetchInterval 기능 수행
  });

  const getCurgentAPI = async () => {
    const req = { cCd: userInfo.hCd };
    const res = await apiGet({ path: '/safe/curgent', req });
    const { statusCode, data } = res.data;

    if (statusCode === 200) {
      const filteredData = data
        .filter((el: any) => el.useYn === 'Y') // 사용유무 Y인 것만 필터링
        .filter((el: any) => dayjs(el.cDate).isSameOrBefore(dayjs(), 'day')); // 오늘 날짜 이하만 필터링
      return filteredData;
    }

    return [];
  };

  useEffect(() => {
    // let handleAnimationEnd: (event: AnimationEvent) => void;
    let targetElement: HTMLDivElement;
    let currentAnimation: Animation | null = null;
    let animationTimeout: NodeJS.Timeout | null = null;

    const checkElement = () => {
      const bodyEl = document.body as HTMLElement;
      targetElement = document.querySelector('.urgent-wrap .react-multi-carousel-item--active .ckeditor') as HTMLDivElement;

      if (targetElement && bodyEl) {
        const zoomValue = Number((bodyEl.style as any).zoom); // ts 버전이 낮아서 zoom에 대한 타입이 명시되어 있지 않음 / 해상도가 커질 때 zoom 값에 의해 스크롤 계산이 잘못되는 부분 방지
        const viewHeight = Math.floor(document.querySelector('.urgent-wrap .react-multi-carousel-item--active .ck-editor-cont')?.getBoundingClientRect().height || 0) + 12; // 뷰 높이 ( +12는 추가 허용 범위 )
        const contentHeight = targetElement?.getBoundingClientRect().height || 0; // 내용 전체 높이
        const fmtSecond = (contentHeight / zoomValue) * 0.02;
        // const contentAnimation = contentHeight > viewHeight - 8 ? `slide ${fmtSecond}s ${ANIMATION_DELAY}s linear` : 'none';

        // 기존 애니메이션 정리
        if (currentAnimation) {
          currentAnimation.cancel();
          activeAnimations.current.delete(targetElement);
        }
        if (animationTimeout) {
          clearTimeout(animationTimeout);
        }

        currentAnimation =
          contentHeight > viewHeight // 컨텐츠 높이가 고정 뷰 높이보다 클 경우 애니메이션 실행
            ? targetElement?.animate([{ transform: 'translateY(0)' }, { transform: `translateY(calc(${(contentHeight / zoomValue) * -1}px + ${viewHeight / zoomValue - 6}px))` }], {
                duration: Number(fmtSecond) * 1000,
                delay: ANIMATION_DELAY * 1000,
                easing: 'linear',
                fill: 'both',
              })
            : null;

        // 새로운 애니메이션을 Map에 저장
        if (currentAnimation && targetElement) {
          activeAnimations.current.set(targetElement, currentAnimation);
        }

        currentAnimation?.addEventListener('finish', () => {
          // 3초 대기 후 애니메이션 재시작
          animationTimeout = setTimeout(() => {
            currentAnimation?.play();
          }, 3000);
        });
      } else {
        // 요소를 찾지 못했을 때 요소 탐색 재시도 - caurosel 라이브러리 사용시 로드될 때 요소를 못찾기 때문
        setTimeout(checkElement, 100);
      }
    };

    checkElement();
    window.addEventListener('resize', checkElement);

    // cleanup 함수 - 컴포넌트 언마운트 시 애니메이션 정리
    return () => {
      window.removeEventListener('resize', checkElement);

      if (currentAnimation) {
        currentAnimation.cancel();
      }
      if (animationTimeout) {
        clearTimeout(animationTimeout);
      }
      // 모든 활성 애니메이션 정리
      activeAnimations.current.forEach((animation) => {
        animation.cancel();
      });
      activeAnimations.current.clear();
    };
  }, [curIdx]);

  /* 리프레시 버튼 클릭 이벤트
  const onClickRefresh = () => {
    refetch();
  };
  */

  const responsive = useMemo(
    () => ({
      desktop: {
        breakpoint: { max: 3000, min: 1024 },
        items: 1,
        // items: size === 'large' ? carouselCount : 1,
        slidesToSlide: 1, // optional, default to 1.
      },
      tablet: {
        breakpoint: { max: 1024, min: 464 },
        items: 1,
        // items: size === 'large' ? 2 : 1,
        slidesToSlide: 1, // optional, default to 1.
      },
      mobile: {
        breakpoint: { max: 464, min: 0 },
        items: 1,
        slidesToSlide: 1, // optional, default to 1.
      },
    }),
    []
  );

  const isSwiping = urgent && urgent.length > 1;
  const loading = !urgent || isLoading;
  if (isError) return <IssueGuide />;

  return (
    <Root className='urgent-wrap'>
      <div className='widget-headers'>
        <div className='widget-title flex-between'>
          <span>
            {t(name)}
            <span className='badge'>{urgent?.length ?? 0}</span>
          </span>
          {/* 리프레시 버튼
          <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
            <span className='material-symbols-rounded'>refresh</span>
          </div>
          */}
        </div>
      </div>
      {loading ? (
        <div style={{ width: '100%', height: '100%' }}>
          <SkeletonTable rows={3} />
        </div>
      ) : (
        <div className='widget-body card widget-urgent-notice'>
          {urgent.length > 0 ? (
            <Carousel
              swipeable={isSwiping}
              draggable={isSwiping}
              responsive={responsive}
              infinite={isSwiping}
              autoPlay={isSwiping}
              autoPlaySpeed={60000}
              keyBoardControl
              transitionDuration={500}
              containerClass='carousel-container'
              removeArrowOnDeviceType={['tablet', 'mobile', 'desktop']}
              dotListClass='custom-dot-list-style'
              showDots={isSwiping}
              customDot={<CustomDot mergedData={urgent} />}
              afterChange={(previousSlide, { currentSlide, onMove }: any) => {
                setCurIdx(currentSlide);

                // Carousel 슬라이드 변경시 애니메이션 초기화
                const targetAll: NodeListOf<HTMLDivElement> | null = document.querySelectorAll('.urgent-wrap .react-multi-carousel-item');

                if (targetAll) {
                  targetAll.forEach((nodeEl: HTMLDivElement) => {
                    const childNode = nodeEl.querySelector('.ckeditor') as HTMLDivElement | null;
                    if (childNode) {
                      // childNode.style.animation = 'none';
                      // animate() API로 생성된 애니메이션 정리
                      const animation = activeAnimations.current.get(childNode);
                      if (animation) {
                        animation.cancel();
                        activeAnimations.current.delete(childNode);
                      }
                    }
                  });
                }
              }}
            >
              {urgent.map((el: any, index: number) => (
                <div key={el.cSeq} className='notice-box'>
                  <div className='notice-header'>
                    <h4>{el.cTitle}</h4>
                    <div className='notice-info'>
                      <span>{el.cDate}</span>
                      {/* el.writer && (
                        <>
                          <b> · </b>
                          <span>{el.writer}</span>
                        </>
                      ) */}
                    </div>
                  </div>

                  <div className='ck-editor-cont'>
                    <div
                      ref={(item: any) => {
                        ckRefs.current[index] = item;
                      }}
                    >
                      <TextEditor content={el.cContents} readOnly idx={index} />
                    </div>
                  </div>
                </div>
              ))}
            </Carousel>
          ) : (
            <div className='no-data'>
              <IssueGuide text='긴급 공지가 없습니다' />
            </div>
          )}
        </div>
      )}
    </Root>
  );
};

export default UrgentNotice;
